import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Button, TextField, IconButton, Box, Paper
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW } from '../../styles/recruiterTheme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page Wrapper ─────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  font-family: ${FONT_BODY};
  background: #F3EEF9;
  min-height: 100vh;
  padding-bottom: 40px;
`;

// ─── Header ───────────────────────────────────────────────────────────────────
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px 32px;
  background: #fff;
  border-bottom: 1.5px solid #EDE7F6;
  box-shadow: 0 2px 12px rgba(79,55,139,0.07);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const BackBtn = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 36px; height: 36px;
    transition: all 0.2s;
    flex-shrink: 0;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
    margin-top: 4px !important;
  }
`;

// ─── Stats Row ────────────────────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  padding: 24px 32px 0 32px;
  animation: ${fadeSlideIn} 0.4s ease both;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg,#6750A4,#4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 88px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 14px ${p => p.shadow || 'rgba(103,80,164,0.24)'};
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 20px ${p => p.shadow || 'rgba(103,80,164,0.34)'}; }
  &::after {
    content: '';
    position: absolute;
    top: -18px; right: -18px;
    width: 60px; height: 60px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 4px;
`;

const StatLbl = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIconWrap = styled.div`
  position: absolute;
  top: 50%; right: 16px;
  transform: translateY(-50%);
  opacity: 0.2;
  svg { font-size: 32px; }
`;

// ─── Content ──────────────────────────────────────────────────────────────────
const ContentArea = styled.div`
  padding: 24px 32px;
  animation: ${fadeSlideIn} 0.45s ease 0.05s both;
`;

const TableCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.md}px !important;
    overflow-x: auto;
    box-shadow: ${SHADOW.card} !important;
    border: 1px solid ${COLORS.border};
  }
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    text-align: left;
    vertical-align: middle;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.15s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: #F3EEF9 !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: ${COLORS.ink} !important;
    padding: 12px 16px !important;
    border-bottom: 1px solid #F0EAF9 !important;
    vertical-align: middle;
  }
`;

// ─── Summary Text ─────────────────────────────────────────────────────────────
const SummaryWrap = styled.div`
  background: #FAF7FF;
  border-radius: ${RADIUS.sm}px;
  padding: 12px 16px;
  border: 1px solid ${COLORS.border};
  min-width: 280px;
  max-width: 500px;
`;

const SummaryText = styled.p`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 500;
  color: ${COLORS.ink};
  line-height: 1.5;
  margin: 0;
`;

// ─── Resume Link ──────────────────────────────────────────────────────────────
const ResumeLink = styled.a`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  color: #6750A4;
  text-decoration: underline;
  text-underline-offset: 3px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  svg { flex-shrink: 0; }
  &:hover { color: #4F378B; }
`;

// ─── Action Buttons ───────────────────────────────────────────────────────────
const ActionBtnRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 140px;
`;

const ActionBtn = styled.button`
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.caption}px;
  border-radius: ${RADIUS.sm}px;
  height: 32px;
  padding: 0 16px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.18s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    pointer-events: none;
  }

  &.accept {
    background: ${p => p.active ? 'linear-gradient(135deg,#2E7D32,#1B5E20)' : 'linear-gradient(135deg,#E8F5E9,#C8E6C9)'};
    color: ${p => p.active ? '#fff' : '#2D7D46'};
    box-shadow: ${p => p.active ? '0 3px 10px rgba(46,125,50,0.3)' : 'none'};
    &:hover { transform: translateY(-1px); box-shadow: 0 5px 14px rgba(46,125,50,0.3); }
  }
  &.reject {
    background: ${p => p.active ? 'linear-gradient(135deg,#B71C1C,#C62828)' : 'linear-gradient(135deg,#FDECEA,#FFCDD2)'};
    color: ${p => p.active ? '#fff' : '#B3261E'};
    box-shadow: ${p => p.active ? '0 3px 10px rgba(183,28,28,0.28)' : 'none'};
    &:hover { transform: translateY(-1px); box-shadow: 0 5px 14px rgba(183,28,28,0.28); }
  }
  &.hold {
    background: ${p => p.active ? 'linear-gradient(135deg,#F57F17,#E65100)' : 'linear-gradient(135deg,#FFF8E1,#FFE0B2)'};
    color: ${p => p.active ? '#fff' : '#E65100'};
    box-shadow: ${p => p.active ? '0 3px 10px rgba(245,127,23,0.28)' : 'none'};
    &:hover { transform: translateY(-1px); box-shadow: 0 5px 14px rgba(245,127,23,0.28); }
  }
`;

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  gap: 4px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${p => p.bg};
  color: ${p => p.fg};
  margin-top: 8px;
`;

// ─── Reason Field ─────────────────────────────────────────────────────────────
const ReasonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 220px;
`;

// ─── Go Back Button ───────────────────────────────────────────────────────────
const GoBackBtn = styled(Button)`
  && {
    background: linear-gradient(135deg, #6750A4, #4F378B) !important;
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 36px;
    padding: 0 16px !important;
    text-transform: none !important;
    box-shadow: 0 4px 14px rgba(79,55,139,0.28) !important;
    transition: all 0.2s !important;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 7px 20px rgba(79,55,139,0.38) !important;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  padding: 40px 16px;
`;

// ─────────────────────────────────────────────────────────────────────────────
const safeDecode = (value) => {
  try { return decodeURIComponent(value); } catch { return value; }
};

function ResumeDisplay({ resumeData: resumeDataProp }) {
  const { state } = useLocation();
  // Prefer the prop (dashboard tab), fall back to router state (/resumedisplay route)
  const source = resumeDataProp || state || {};
  const encodedResumePaths = source.resumePaths;
  const summaries = source.summaries || [];
  const [errorMessages, setErrorMessages] = useState({});
  const jdId = source.jdId;
  const navigate = useNavigate();
  const [acceptanceStatus, setAcceptanceStatus] = useState({});
  const [busyRows, setBusyRows] = useState({});

  const getpdfApiUrl = process.env.REACT_APP_GET_PDF_API || "http://127.0.0.1:5001/get_pdf?source_path";
  const processResumeApiUrl = process.env.REACT_APP__RECRUITER_RESUMEPROCESS_API;

  // A plain link can't carry the login token the model API requires, so fetch the
  // file (axios adds the token) and open it from a blob URL.
  const openResume = async (resumePath) => {
    const win = window.open('', '_blank');
    try {
      const res = await axios.get(`${getpdfApiUrl}=${encodeURIComponent(resumePath)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      if (win) win.close();
      alert('Could not open the resume. Please try again.');
    }
  };

  const resumePaths = Array.isArray(encodedResumePaths)
    ? encodedResumePaths.filter(Boolean).map((path) => safeDecode(path))
    : (encodedResumePaths ? [safeDecode(encodedResumePaths)] : []);

  const resumeNames = resumePaths.map((resumePath) => {
    const lastSlashIndex = resumePath.lastIndexOf('/');
    return resumePath.substring(lastSlashIndex + 1);
  });

  const [acceptedResumes, setAcceptedResumes] = useState([]);

  const parseResumeData = (resumeName) => ({ profileUrl: '', resumeUrl: `${resumeName}` });

  const handleBack = () => navigate(-1);

  const handleResume = async (resumeIndex, jdId, action) => {
    if (busyRows[resumeIndex]) return;
    const { resumeUrl } = parseResumeData(resumeNames[resumeIndex]);
    const reasonInput = document.getElementById(`reason-for-${resumeIndex}`).value;
    const wordCount = reasonInput.split(/\s+/).length;

    if (reasonInput.trim() === "") {
      alert("Please enter a reason before accepting or rejecting.");
      return;
    }
    if (wordCount < 2 || wordCount > 50) {
      setErrorMessages((prev) => ({ ...prev, [resumeIndex]: 'Reason must be between 2 and 50 words.' }));
      return;
    } else {
      setErrorMessages((prev) => ({ ...prev, [resumeIndex]: '' }));
    }

    setBusyRows(prev => ({ ...prev, [resumeIndex]: true }));
    try {
      const response1 = await axios.get(`${getpdfApiUrl}=${encodeURIComponent(resumePaths[resumeIndex])}`, { responseType: 'arraybuffer' });
      if (response1.status !== 200) {
        window.alert('Failed to fetch resume from the backend API.');
        return;
      }
      const extractedEmail = response1.headers['extractedemail'];
      const extractedPhoneNumber = response1.headers['extractedphonenumber'];
      const extractedName = response1.headers['extractedname'];
      const resumeData = {
        resumeUrl,
        Extracted_Email: extractedEmail,
        extracted_phone_number: extractedPhoneNumber,
        jobDetailsId: jdId,
        action,
        recruiterFeedback: reasonInput,
        extracted_name: extractedName,
        address: '1233',
        summaries: summaries[resumeIndex],
      };
      const response2 = await fetch(processResumeApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(resumeData),
      });
      const responseData = await response2.json().catch(() => ({}));
      if (!response2.ok) {
        console.error('Error response:', responseData);
        window.alert(`Failed to ${action} resume: ${responseData.message || responseData.error || response2.statusText || 'unknown error'}`);
        return;
      }
      if (responseData.success && responseData.isAccepted) {
        alert('This resume is already accepted.');
        return;
      }

      if (action === 'accept')       setAcceptanceStatus(prev => ({ ...prev, [resumeIndex]: 'Accepted' }));
      else if (action === 'reject')  setAcceptanceStatus(prev => ({ ...prev, [resumeIndex]: 'Rejected' }));
      else if (action === 'hold')    setAcceptanceStatus(prev => ({ ...prev, [resumeIndex]: 'On_Hold' }));

    } catch (error) {
      console.error('Error fetching or processing resume:', error);
      window.alert('Failed to fetch or process resume from the backend API.');
    } finally {
      setBusyRows(prev => ({ ...prev, [resumeIndex]: false }));
    }
  };

  // ─── Computed stats ────────────────────────────────────────────────────────
  const totalCount   = resumePaths.length;
  const acceptedCount = Object.values(acceptanceStatus).filter(s => s === 'Accepted').length;
  const rejectedCount = Object.values(acceptanceStatus).filter(s => s === 'Rejected').length;
  const holdCount    = Object.values(acceptanceStatus).filter(s => s === 'On_Hold').length;
  const pendingCount = totalCount - acceptedCount - rejectedCount - holdCount;

  const getStatusBadge = (status) => {
    if (status === 'Accepted') return { bg: '#E6F4EA', fg: '#2D7D46', icon: <CheckCircleIcon sx={{ fontSize: 13 }} />, label: 'Accepted' };
    if (status === 'Rejected') return { bg: '#FDECEA', fg: '#B3261E', icon: <CancelIcon sx={{ fontSize: 13 }} />, label: 'Rejected' };
    if (status === 'On_Hold')  return { bg: '#FFF3E0', fg: '#E65100', icon: <PauseCircleIcon sx={{ fontSize: 13 }} />, label: 'On Hold' };
    return null;
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={recruiterTheme}>
    <PageWrapper>
      {/* Header */}
      <HeaderBar>
        <BackBtn onClick={handleBack}><ArrowBackIcon /></BackBtn>
        <Box sx={{ minWidth: 0 }}>
          <PageTitle>Candidate Resumes</PageTitle>
          <PageSubtitle>Review, evaluate and take action on candidate submissions</PageSubtitle>
        </Box>
      </HeaderBar>

      {/* Stat Cards */}
      <StatsRow>
        <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.26)">
          <StatNum>{totalCount}</StatNum>
          <StatLbl>Total Resumes</StatLbl>
          <StatIconWrap><PeopleAltIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#F57F17,#E65100)" shadow="rgba(245,127,23,0.22)">
          <StatNum>{pendingCount}</StatNum>
          <StatLbl>Pending Review</StatLbl>
          <StatIconWrap><HourglassTopIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#2E7D32,#1B5E20)" shadow="rgba(46,125,50,0.22)">
          <StatNum>{acceptedCount}</StatNum>
          <StatLbl>Accepted</StatLbl>
          <StatIconWrap><CheckCircleIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#B71C1C,#C62828)" shadow="rgba(183,28,28,0.2)">
          <StatNum>{rejectedCount}</StatNum>
          <StatLbl>Rejected</StatLbl>
          <StatIconWrap><CancelIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#F57F17,#BF360C)" shadow="rgba(191,54,12,0.2)">
          <StatNum>{holdCount}</StatNum>
          <StatLbl>On Hold</StatLbl>
          <StatIconWrap><PauseCircleIcon /></StatIconWrap>
        </StatCard>
      </StatsRow>

      {/* Table */}
      <ContentArea>
        {resumePaths.length > 0 ? (
          <TableCard>
            <Table>
              <TableHead>
                <TableRow>
                  <HeadCell sx={{ textAlign: 'right', width: 48 }}>#</HeadCell>
                  <HeadCell>Resume Summary</HeadCell>
                  <HeadCell>Resume File</HeadCell>
                  <HeadCell>Reason (2–50 words)</HeadCell>
                  <HeadCell sx={{ textAlign: 'right' }}>Actions</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resumePaths.map((resumePath, index) => {
                  const badge = getStatusBadge(acceptanceStatus[index]);
                  return (
                    <BodyRow key={index}>
                      {/* # */}
                      <BodyCell sx={{ fontWeight: '700 !important', color: '#8B7DB5 !important', textAlign: 'right', width: '48px', fontVariantNumeric: 'tabular-nums' }}>
                        {index + 1}
                      </BodyCell>

                      {/* Summary */}
                      <BodyCell>
                        {summaries[index] ? (
                          <SummaryWrap>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <DescriptionIcon sx={{ fontSize: 14, color: '#6750A4' }} />
                              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.overline, color: '#4F378B', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                Summary
                              </Typography>
                            </Box>
                            <SummaryText>{summaries[index]}</SummaryText>
                          </SummaryWrap>
                        ) : (
                          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: '#B0A0D0', fontStyle: 'italic' }}>
                            No summary available
                          </Typography>
                        )}
                      </BodyCell>

                      {/* Resume file link */}
                      <BodyCell>
                        <ResumeLink
                          title={resumeNames[index]}
                          href={`${getpdfApiUrl}=${encodeURIComponent(resumePath)}`}
                          onClick={(e) => { e.preventDefault(); openResume(resumePath); }}
                          target="_blank" rel="noopener noreferrer">
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                          {resumeNames[index]}
                        </ResumeLink>
                      </BodyCell>

                      {/* Reason input */}
                      <BodyCell>
                        <ReasonWrap>
                          <TextField
                            id={`reason-for-${index}`}
                            variant="outlined"
                            placeholder="Enter feedback reason…"
                            size="small"
                            required
                            multiline
                            rows={3}
                            sx={{
                              width: '260px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: `${RADIUS.sm}px`,
                                fontFamily: FONT_BODY,
                                fontSize: TYPE.small,
                                '&:hover fieldset': { borderColor: '#7C5CBF' },
                                '&.Mui-focused fieldset': { borderColor: '#6750A4' },
                              },
                              '& .MuiInputLabel-root': { fontFamily: FONT_BODY },
                            }}
                          />
                          {errorMessages[index] && (
                            <Typography sx={{ color: COLORS.error, fontSize: TYPE.caption, fontFamily: FONT_BODY, fontWeight: 600 }}>
                              {errorMessages[index]}
                            </Typography>
                          )}
                        </ReasonWrap>
                      </BodyCell>

                      {/* Actions + status badge */}
                      <BodyCell sx={{ textAlign: 'right' }}>
                        <ActionBtnRow style={{ marginLeft: 'auto', width: 140 }}>
                          <ActionBtn
                            className="accept"
                            active={acceptanceStatus[index] === 'Accepted' ? 1 : 0}
                            disabled={!!busyRows[index]}
                            onClick={() => handleResume(index, jdId, 'accept')}
                          >
                            <CheckCircleIcon sx={{ fontSize: 14 }} />
                            {acceptanceStatus[index] === 'Accepted' ? 'Accepted ✓' : 'Accept'}
                          </ActionBtn>
                          <ActionBtn
                            className="reject"
                            active={acceptanceStatus[index] === 'Rejected' ? 1 : 0}
                            disabled={!!busyRows[index]}
                            onClick={() => handleResume(index, jdId, 'reject')}
                          >
                            <CancelIcon sx={{ fontSize: 14 }} />
                            {acceptanceStatus[index] === 'Rejected' ? 'Rejected ✓' : 'Reject'}
                          </ActionBtn>
                          <ActionBtn
                            className="hold"
                            active={acceptanceStatus[index] === 'On_Hold' ? 1 : 0}
                            disabled={!!busyRows[index]}
                            onClick={() => handleResume(index, jdId, 'hold')}
                          >
                            <PauseCircleIcon sx={{ fontSize: 14 }} />
                            {acceptanceStatus[index] === 'On_Hold' ? 'On Hold ✓' : 'Hold'}
                          </ActionBtn>

                          {badge && (
                            <StatusBadge bg={badge.bg} fg={badge.fg}>
                              {badge.icon}
                              {badge.label}
                            </StatusBadge>
                          )}
                        </ActionBtnRow>
                      </BodyCell>
                    </BodyRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableCard>
        ) : (
          <EmptyState>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.cardTitle, lineHeight: 1.25, fontWeight: 700, color: '#C4B0E8' }}>
              No resumes to display
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: COLORS.muted }}>
              No resume paths were provided.
            </Typography>
          </EmptyState>
        )}

        {/* Go Back */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
          <GoBackBtn onClick={handleBack} startIcon={<ArrowBackIcon />}>
            Go Back
          </GoBackBtn>
        </Box>
      </ContentArea>
    </PageWrapper>
    </ThemeProvider>
  );
}

export default ResumeDisplay;
